import { Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useUser } from '@/context/user-context';
import type * as Types from '../../../graphql/types';
import { UserRole } from '../../../graphql/types';
import { useSubscriptionManagement } from '../hooks/use-subscription-management';
import { ReportSubscriptionCard } from './report-subscription-card';
import { ReportSubscriptionForm } from './report-subscription-form';
import { ReportSubscriptionPreferencesModal } from './report-subscription-preferences-modal';

interface ReportSubscriptionsListProps {
  className?: string;
  brandCode?: string; // When provided, scope subscriptions to this hotel
}

export const ReportSubscriptionsList: React.FC<ReportSubscriptionsListProps> = ({
  className = '',
  brandCode,
}) => {
  const { user } = useUser();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const {
    subscriptions,
    loading,
    isOperating,
    selectedSubscription,
    isPreferencesModalOpen,
    handleUpdatePreferences,
    handleCreateSubscription,
    handleDeleteSubscription,
    handleTriggerReport,
    openPreferencesModal,
    closePreferencesModal,
  } = useSubscriptionManagement({
    brandCode,
    onSuccess: (message) => {
      toast.success(message);
      setShowCreateForm(false);
      setDeleteConfirmId(null);
    },
    onError: (message) => {
      toast.error(message);
    },
  });

  const currentUserId = user?.id ? parseInt(String(user.id), 10) : null;
  const canManageAll = user?.role === UserRole.Admin || user?.role === UserRole.Manager;

  const canManageSubscription = (subscription: Types.ReportSubscription) => {
    if (canManageAll) return true;
    if (currentUserId === null) return false;
    return subscription.rmUserId === currentUserId;
  };

  // Handlers
  const handleToggleActive = async (id: string, isActive: boolean) => {
    const subscription = subscriptions.find((sub) => sub?.id === id);
    if (!subscription) return;

    const canManage = canManageSubscription(subscription);
    if (!canManage) return;

    // Use updatePreferences with id to ensure correct subscription is toggled
    // (handleUnsubscribe only uses brandCode, which is ambiguous when multiple subscriptions exist)
    await handleUpdatePreferences(subscription.id, { isActive });
  };

  const handleDelete = async (id: string) => {
    if (deleteConfirmId === id) {
      // Confirm delete
      const subscription = subscriptions.find((sub) => sub?.id === id);
      if (!subscription) return;
      if (!canManageSubscription(subscription)) return;
      await handleDeleteSubscription(id);
    } else {
      // Show confirmation
      setDeleteConfirmId(id);
      // Auto-clear confirmation after 5 seconds
      setTimeout(() => {
        setDeleteConfirmId(null);
      }, 5000);
    }
  };

  const handleCreateFormSubmit = async (data: Types.CreateReportSubscriptionInput) => {
    await handleCreateSubscription(data);
  };

  const handlePreferencesUpdate = async (
    id: string,
    input: Types.UpdateReportSubscriptionInput,
  ) => {
    await handleUpdatePreferences(id, input);
  };

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className='flex items-center justify-center py-8'>
          <Loader2 className='h-6 w-6 animate-spin' />
          <span className='ml-2'>Loading subscriptions...</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header */}
      <Card>
        <CardHeader className='flex flex-row items-center justify-between space-y-0'>
          <div>
            <CardTitle>Automated Report Subscriptions</CardTitle>
            <CardDescription>
              Manage your automated report subscriptions for different hotels
            </CardDescription>
          </div>
          {!showCreateForm && (
            <Button onClick={() => setShowCreateForm(true)} disabled={isOperating}>
              <Plus className='mr-2 h-4 w-4' />
              Add Subscription
            </Button>
          )}
        </CardHeader>

        {/* Create Form */}
        {showCreateForm && (
          <CardContent>
            <ReportSubscriptionForm
              brandCode={brandCode} // Pass brandCode for hotel scoping
              onSubmit={handleCreateFormSubmit}
              onCancel={() => setShowCreateForm(false)}
              loading={isOperating}
              title='Add Automated Report Subscription'
              description='Create a new subscription to receive automated reports'
            />
          </CardContent>
        )}
      </Card>

      {/* Subscriptions List */}
      {subscriptions.length === 0 ? (
        <Card>
          <CardContent className='py-8 text-center'>
            <div className='mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4'>
              <Plus className='h-8 w-8 text-gray-400' />
            </div>
            <h3 className='text-lg font-medium text-gray-900 mb-2'>No subscriptions yet</h3>
            <p className='text-gray-500 mb-4'>
              Get started by creating your first automated report subscription
            </p>
            {!showCreateForm && (
              <Button onClick={() => setShowCreateForm(true)}>Create Subscription</Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
          {subscriptions.map((subscription) => (
            <div key={subscription?.id} className='relative'>
              <ReportSubscriptionCard
                subscription={subscription}
                onToggleActive={handleToggleActive}
                onEditPreferences={(id) => {
                  if (!subscription) return;
                  if (!canManageSubscription(subscription)) return;
                  openPreferencesModal(id);
                }}
                onDelete={(id) => {
                  if (!subscription) return;
                  if (!canManageSubscription(subscription)) return;
                  handleDelete(id);
                }}
                onRunNow={(id) => {
                  if (!subscription) return;
                  handleTriggerReport(id);
                }}
                loading={isOperating}
                canManage={!!subscription && canManageSubscription(subscription)}
                canRunNow={canManageAll}
              />

              {/* Delete Confirmation Overlay */}
              {deleteConfirmId === subscription?.id && (
                <div className='absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center'>
                  <Card className='w-64'>
                    <CardContent className='p-4 text-center'>
                      <h4 className='font-medium mb-2'>Confirm Delete</h4>
                      <p className='text-sm text-muted-foreground mb-4'>
                        This will permanently delete your subscription. This action cannot be
                        undone.
                      </p>
                      <div className='flex space-x-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          onClick={() => setDeleteConfirmId(null)}
                          className='flex-1'
                        >
                          Cancel
                        </Button>
                        <Button
                          variant='destructive'
                          size='sm'
                          onClick={() => handleDelete(subscription.id)}
                          disabled={isOperating}
                          className='flex-1'
                        >
                          Delete
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Preferences Modal */}
      {isPreferencesModalOpen && selectedSubscription && (
        <ReportSubscriptionPreferencesModal
          subscription={selectedSubscription}
          open={isPreferencesModalOpen}
          onClose={closePreferencesModal}
          onSave={handlePreferencesUpdate}
          loading={isOperating}
        />
      )}
    </div>
  );
};
